/**
 * SMART MEMORY EXPIRATION FIXES
 * Update files to expire memories on event date, not just 7 days
 * 
 * FILES TO UPDATE:
 * 1. GoalLifecycleService.js (COMPLETE REWRITE BELOW)
 * 2. ConversationMemorySystem.js (FIND & REPLACE)
 * 3. TimeAwareFollowupEngine.js (FIND & REPLACE)
 */

// ============================================
// FILE 1: GoalLifecycleService.js
// COMPLETE REPLACEMENT - Copy entire file
// ============================================

import { collection, addDoc, query, where, getDocs, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/firebase';

export class GoalLifecycleService {
  constructor(firestoreService, memoryService) {
    this.firestoreService = firestoreService;
    this.memoryService = memoryService;
    this.db = db;
  }

  // ============================================
  // 1. DETECT GOAL FROM USER MESSAGE
  // ============================================
  
  detectGoal(userMessage) {
    if (!userMessage || userMessage.length < 5) return null;

    const lower = userMessage.toLowerCase();

    // Detect TIMEFRAME
    let timeframe = null;
    if (lower.match(/today|tomorrow|tonight|this evening/)) {
      timeframe = "SHORT";
    } else if (lower.match(/this week|next week|within|days|by next|in \d+ weeks/)) {
      timeframe = "MEDIUM";
    } else if (lower.match(/want to|my goal|always|every|ongoing|each|forever|main goal|trying to/)) {
      timeframe = "LONG";
    }

    if (!timeframe) return null;

    // Detect TYPE
    let type = null;
    if (lower.match(/every|each|daily|weekly|monthly|habit|regularly|remind me to|each [a-z]+day/)) {
      type = "HABIT";
    } else if (lower.match(/remind|call|book|appointment|remember to|need to/)) {
      type = "REMINDER";
    } else if (lower.match(/finish|pass|complete|reach|achieve \d+|learn/)) {
      type = "MILESTONE";
    } else if (lower.match(/want|goal|trying|improve|get better|become/)) {
      type = "GOAL";
    }

    if (!type) type = "GOAL";

    console.log('🎯 Goal detected:', { timeframe, type, preview: userMessage.substring(0, 50) });

    return { timeframe, type, detected: true };
  }

  // ============================================
  // 2. SAVE GOAL TO FIRESTORE
  // ============================================

  async saveGoal(userMessage, detectedGoal, userId) {
    try {
      if (!this.firestoreService?.isReady()) {
        console.warn('⚠️ Firestore not ready');
        return null;
      }

      const goalRef = collection(this.db, 'users', userId, 'goals');

      const goalData = {
        content: userMessage.substring(0, 300),
        timeframe: detectedGoal.timeframe,
        type: detectedGoal.type,
        status: "ACTIVE",
        createdAt: serverTimestamp(),
        lastCheckIn: null,
        nextCheckIn: this.calculateNextCheckIn(detectedGoal, userMessage),
        userEngagement: "high",
        needsSupport: false,
        isIntegrated: false,
        progressNotes: "",
        eventEndDate: this.extractEventEndDate(userMessage) // ✅ NEW
      };

      const docRef = await addDoc(goalRef, goalData);
      
      console.log('✅ Goal saved:', {
        id: docRef.id,
        timeframe: detectedGoal.timeframe,
        type: detectedGoal.type,
        eventEndDate: goalData.eventEndDate // ✅ NEW
      });

      return { id: docRef.id, ...goalData };

    } catch (error) {
      console.error('❌ Failed to save goal:', error);
      return null;
    }
  }

  // ============================================
  // 3. CALCULATE NEXT CHECK-IN DATE
  // ============================================

  calculateNextCheckIn(goal, userMessage) {
    const now = new Date();
    const eventEndDate = this.extractEventEndDate(userMessage);

    if (goal.timeframe === "SHORT") {
      // If event has specific date, check day before
      if (eventEndDate) {
        const checkDate = new Date(eventEndDate);
        checkDate.setDate(checkDate.getDate() - 1);
        checkDate.setHours(9, 0, 0, 0);
        return checkDate;
      }
      // Otherwise check tomorrow
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      return tomorrow;
    }

    if (goal.timeframe === "MEDIUM") {
      // If event has specific date, check week before
      if (eventEndDate) {
        const checkDate = new Date(eventEndDate);
        checkDate.setDate(checkDate.getDate() - 7);
        checkDate.setHours(9 + Math.floor(Math.random() * 8), Math.floor(Math.random() * 60), 0, 0);
        return checkDate;
      }
      // Check week 2-3 (random day)
      const randomDays = 14 + Math.floor(Math.random() * 7);
      const checkDate = new Date(now);
      checkDate.setDate(checkDate.getDate() + randomDays);
      checkDate.setHours(9 + Math.floor(Math.random() * 8), Math.floor(Math.random() * 60), 0, 0);
      return checkDate;
    }

    if (goal.timeframe === "LONG") {
      // Check every 1-2 weeks (random)
      const randomDays = 7 + Math.floor(Math.random() * 7);
      const checkDate = new Date(now);
      checkDate.setDate(checkDate.getDate() + randomDays);
      checkDate.setHours(9 + Math.floor(Math.random() * 8), Math.floor(Math.random() * 60), 0, 0);
      return checkDate;
    }
  }

  // ============================================
  // 4. EXTRACT EVENT END DATE (NEW METHOD)
  // ============================================

  extractEventEndDate(goalText) {
    if (!goalText) return null;
    
    const datePatterns = [
      { regex: /(\d{1,2})\/(\d{1,2})\/(\d{4})/, format: 'slash' },     // 12/25/2024
      { regex: /(\d{4})-(\d{1,2})-(\d{1,2})/, format: 'dash' },         // 2024-12-25
      { regex: /next\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i, format: 'day' },
      { regex: /in\s+(\d+)\s+(days?|weeks?|months?)/, format: 'relative' }
    ];

    for (let pattern of datePatterns) {
      const match = goalText.match(pattern.regex);
      if (match) {
        return this.parseDetectedDate(match, pattern.format);
      }
    }
    return null;
  }

  // ============================================
  // 5. PARSE DETECTED DATE (NEW METHOD)
  // ============================================

  parseDetectedDate(match, format) {
    const now = new Date();
    
    if (format === 'slash') {
      // MM/DD/YYYY format
      return new Date(match[3], match[1] - 1, match[2]);
    }
    
    if (format === 'dash') {
      // YYYY-MM-DD format
      return new Date(match[1], match[2] - 1, match[3]);
    }
    
    if (format === 'day') {
      // "next monday", "next friday", etc.
      return this.getNextDayOfWeek(match[1].toLowerCase());
    }
    
    if (format === 'relative') {
      // "in 3 weeks", "in 2 days", etc.
      const amount = parseInt(match[1]);
      const unit = match[2].toLowerCase();
      const result = new Date(now);
      
      if (unit.includes('day')) {
        result.setDate(result.getDate() + amount);
      } else if (unit.includes('week')) {
        result.setDate(result.getDate() + (amount * 7));
      } else if (unit.includes('month')) {
        result.setMonth(result.getMonth() + amount);
      }
      
      return result;
    }
    
    return null;
  }

  // ============================================
  // 6. GET NEXT DAY OF WEEK (NEW METHOD)
  // ============================================

  getNextDayOfWeek(dayName) {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const targetDay = days.indexOf(dayName);
    const now = new Date();
    const current = now.getDay();
    
    let daysAhead = targetDay - current;
    if (daysAhead <= 0) daysAhead += 7;
    
    const result = new Date(now);
    result.setDate(result.getDate() + daysAhead);
    return result;
  }

  // ============================================
  // 7. GET GOALS DUE FOR CHECK-IN TODAY
  // ============================================

  async getGoalsDueForCheckIn(userId) {
    try {
      if (!this.firestoreService?.isReady()) return [];

      const goalsRef = collection(this.db, 'users', userId, 'goals');
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const q = query(
        goalsRef,
        where('status', 'in', ['ACTIVE', 'PROGRESSING', 'STRUGGLING']),
        where('nextCheckIn', '>=', today),
        where('nextCheckIn', '<', tomorrow)
      );

      const snapshot = await getDocs(q);
      const goals = [];

      snapshot.forEach(doc => {
        goals.push({
          id: doc.id,
          ...doc.data()
        });
      });

      console.log(`📋 Goals due today: ${goals.length}`);
      return goals;

    } catch (error) {
      console.error('Failed to load goals:', error);
      return [];
    }
  }

  // ============================================
  // 8. BUILD GOAL CONTEXT FOR AI
  // ============================================

  async buildGoalContextForAI(userId) {
    try {
      if (!this.firestoreService?.isReady()) return null;

      const goalsRef = collection(this.db, 'users', userId, 'goals');
      const q = query(
        goalsRef,
        where('status', 'in', ['ACTIVE', 'PROGRESSING', 'STRUGGLING', 'INTEGRATED'])
      );

      const snapshot = await getDocs(q);
      const goals = [];

      snapshot.forEach(doc => {
        goals.push({
          id: doc.id,
          ...doc.data()
        });
      });

      if (goals.length === 0) return null;

      // Group by status
      const byStatus = {
        ACTIVE: [],
        PROGRESSING: [],
        STRUGGLING: [],
        INTEGRATED: []
      };

      goals.forEach(goal => {
        if (byStatus[goal.status]) {
          byStatus[goal.status].push(goal);
        }
      });

      // Build context for AI
      let context = '\n\n=== 🎯 USER GOALS & TRACKING ===\n\n';

      if (byStatus.PROGRESSING.length > 0) {
        context += '🚀 PROGRESSING:\n';
        byStatus.PROGRESSING.forEach(g => {
          context += `  • [${g.timeframe}] [${g.type}] ${g.content}\n`;
        });
        context += '\n→ Ask about progress, show encouragement\n\n';
      }

      if (byStatus.STRUGGLING.length > 0) {
        context += '🆘 STRUGGLING:\n';
        byStatus.STRUGGLING.forEach(g => {
          context += `  • [${g.timeframe}] [${g.type}] ${g.content}\n`;
        });
        context += '\n→ Offer support, ask what\'s blocking\n\n';
      }

      if (byStatus.ACTIVE.length > 0) {
        context += '⚡ ACTIVE:\n';
        byStatus.ACTIVE.forEach(g => {
          context += `  • [${g.timeframe}] [${g.type}] ${g.content}\n`;
        });
        context += '\n→ Check in naturally if relevant\n\n';
      }

      if (byStatus.INTEGRATED.length > 0) {
        context += '✨ INTEGRATED (background):\n';
        byStatus.INTEGRATED.forEach(g => {
          context += `  • ${g.content}\n`;
        });
        context += '\n→ Part of identity, rarely mention\n\n';
      }

      return context;

    } catch (error) {
      console.error('Failed to build goal context:', error);
      return null;
    }
  }

  // ============================================
  // 9. UPDATE GOAL STATUS
  // ============================================

  async updateGoalStatus(userId, goalId, newStatus, notes = '') {
    try {
      if (!this.firestoreService?.isReady()) return null;

      const goalRef = doc(this.db, 'users', userId, 'goals', goalId);

      const updateData = {
        status: newStatus,
        lastCheckIn: serverTimestamp()
      };

      if (notes) updateData.progressNotes = notes;

      // Calculate next check-in based on new status
      if (newStatus === 'PROGRESSING') {
        const nextDate = new Date();
        nextDate.setDate(nextDate.getDate() + (7 + Math.floor(Math.random() * 7)));
        updateData.nextCheckIn = nextDate;
      } else if (newStatus === 'INTEGRATED') {
        // Reduce check-in frequency
        const nextDate = new Date();
        nextDate.setDate(nextDate.getDate() + (14 + Math.floor(Math.random() * 14)));
        updateData.nextCheckIn = nextDate;
      }

      await updateDoc(goalRef, updateData);

      console.log(`✅ Goal updated: ${goalId} → ${newStatus}`);
      return true;

    } catch (error) {
      console.error('Failed to update goal:', error);
      return false;
    }
  }

  // ============================================
  // 10. CHECK IF GOAL DUE FOR CHECK-IN
  // ============================================

  isGoalDueForCheckIn(goal) {
    const now = new Date();
    const nextCheckIn = goal.nextCheckIn.toDate ? goal.nextCheckIn.toDate() : new Date(goal.nextCheckIn);
    return now >= nextCheckIn;
  }
}

export default GoalLifecycleService;
